import {
    useCallback, useMemo,
} from 'react'
import {
    LaneGroup,
} from 'app/Apps/Pricing/Pricing.types'
import DataContentWrapper from 'app/shared-components/DataContentWrapper'
import useFetchQuery from 'app/hooks/useFetchQuery'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useDelayedLoadOptions from 'app/hooks/useDelayedLoadOptions'
import SkyNetSpreadSheet, {
    getEditedItems, SkyNetSpreadSheetControlPanel, useTypeaheadOptions,
} from 'app/shared-components/SkyNetSpreadSheet'
import {
    LaneFeesBulkItem,
} from 'app/Apps/Pricing/LaneFees/LaneFees.types'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    FilterContext,
    FilterContextType, FilteredData,
} from 'app/hooks/useFilter'
import useLabel from 'app/hooks/useLabel'
import useSyncState from 'app/hooks/useSyncState'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'

import ContractBasisRequests, {
    laneGroupSelectorConversion,
} from 'app/requests/ContractBasis/ContractBasis.requests'
import LaneGroupRequests from 'app/requests/ContractBasis/LaneGroup/LaneGroup.requests'

import filterConfig
    from './LaneFeesSpreadsheet.filterConfig'
import getConfig from './LaneFeesSpreadsheet.config'

export default function LaneFeesSpreadsheet({
    onSave,
    onCancel,
    filterOptions,
}: Readonly<{
    onSave: (value: LaneFeesBulkItem[], options: AsyncMutationOptions) => void
    onCancel: (...args: any[]) => void,
    filterOptions: FilterContextType
}>) {
    const {
        value, setValue,
    } = useSyncState(filterOptions?.filteredData as FilteredData<LaneFeesBulkItem[]>)

    const getLabel = useLabel()
    const getAllowedValues = useEnumValues()

    const getLaneGroup = useFetchQuery({
        requestKey: RequestKeys.getContractBasisLaneGroupsDetails,
        requestFn: LaneGroupRequests.get,
        name: 'LaneGroupDetails',
    })

    const editedItems = useMemo(() => {
        return getEditedItems({
            allItems: value,
            initData: filterOptions?.filteredData,
        })
    }, [
        filterOptions?.filteredData,
        value,
    ])

    const edited = Boolean(editedItems.length)

    const handleSave = useCallback(async (options?: AsyncMutationOptions) => {
        return onSave(editedItems.map((item) => {
            return {
                ...item,
                laneGroupId: item.laneGroup?.id,
            }
        }), options)
    }, [
        editedItems,
        onSave,
    ])

    /* typeahead options */
    const loadLaneGroupOptions = useDelayedLoadOptions({
        ...ContractBasisRequests.filterRequestLaneGroups({
            contractId: filterOptions?.filter?.contractBasisId?.[0],
        }),
        convertion: laneGroupSelectorConversion,
    })
    const laneGroupParams = useTypeaheadOptions({
        load: loadLaneGroupOptions,
        filters: {
            contractId: filterOptions?.filter?.contractBasisId?.[0],
        },
        isFilterRequired: true,
    })

    /* on change actions */
    const onLaneGroupChangeOverride = useCallback(async ({
        id,
    }: { id: number }) => {
        const laneGroup = await getLaneGroup<LaneGroup>({
            id,
        })

        if (laneGroup) {
            const {
                tempRange,
                leaseType,
                deliveryServiceType,
                collectionServiceType,
                originAirportsIataCode,
                destinationAirportsIataCode,
            } = laneGroup

            return {
                tempRange,
                leaseType,
                deliveryServiceType,
                collectionServiceType,
                originAirportsIataCode,
                destinationAirportsIataCode,
                handoverPointLocationName: laneGroup.handoverPoint?.locationName,
                collectionDropoffPointLocationName: laneGroup?.collectionDropoffPoint?.locationName,
            }
        }

        return undefined
    }, [getLaneGroup])

    /* configs */
    const config = useMemo(() => {
        return getConfig({
            addRowMenuOption: true,
            getAllowedValues,
            getLabel,
            laneGroupParams,
            onLaneGroupChangeOverride,
        })
    }, [
        getAllowedValues,
        getLabel,
        laneGroupParams,
        onLaneGroupChangeOverride,
    ])

    const filterContext = useMemo(() => {
        return {
            ...filterOptions,
            config: filterConfig,
            edited,
        }
    }, [
        filterOptions,
        edited,
    ])

    return (
        <FilterContext.Provider value={filterContext}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <DataContentWrapper
                    isEmpty={!(filterOptions?.filter?.contractBasisId)}
                    emptyDataReplacer="Please select the contract basis to add new lane fees"
                >
                    <SkyNetSpreadSheet
                        config={config}
                        value={value}
                        onChange={setValue}
                    />
                </DataContentWrapper>
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}
